import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { User } from 'src/models/user.model';
import { JWT_TOKEN } from '../config/constants';
import { LoginData } from '../models/login.model';

type AuthenticationState = {
  loading: boolean;
  errorMessage: string;
  fetchSuccess: boolean;
  fetchFailure: boolean;
  isAuthenticated: boolean;
  user: User | null;
  initializing: boolean;
};

const initialState: AuthenticationState = {
  loading: false,
  errorMessage: '',
  fetchSuccess: false,
  fetchFailure: false,
  isAuthenticated: false,
  user: null,
  initializing: true,
};

export const doLogin = createAsyncThunk('login/post', async (data: LoginData, { rejectWithValue, dispatch }) => {
  try {
    const response: AxiosResponse<{ token: string; user: User }> = await axios.post(`/api/login`, data);

    const token = response.data.token;
    data.rememberMe ? localStorage.setItem(JWT_TOKEN, token) : sessionStorage.setItem(JWT_TOKEN, token);

    dispatch(getAccount());

    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

export const getAccount = createAsyncThunk('authentication/get', async (_, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<User> = await axios.get(`/api/account`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.message);
  }
});

// Methods

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState: initialState,
  reducers: {
    logout(state) {
      if (localStorage.getItem(JWT_TOKEN)) localStorage.removeItem(JWT_TOKEN);
      if (sessionStorage.getItem(JWT_TOKEN)) sessionStorage.removeItem(JWT_TOKEN);

      return {
        ...state,
        account: null,
        isAuthenticated: false,
      };
    },
    resetAuthState() {
      return initialState;
    },
  },
  extraReducers: builder => {
    builder
      // login
      .addCase(doLogin.pending, (state): AuthenticationState => {
        return {
          ...state,
          loading: true,
          errorMessage: '',
        };
      })
      .addCase(doLogin.fulfilled, (state): AuthenticationState => {
        return {
          ...state,
          loading: false,
          fetchSuccess: true,
        };
      })
      .addCase(doLogin.rejected, (state, { payload }): AuthenticationState => {
        return {
          ...state,
          loading: false,
          fetchFailure: true,
          errorMessage: payload as string,
        };
      })
      // get account
      .addCase(getAccount.pending, (state): AuthenticationState => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(getAccount.fulfilled, (state, { payload }): AuthenticationState => {
        return {
          ...state,
          loading: false,
          fetchSuccess: true,
          user: payload,
          isAuthenticated: true,
          initializing: false,
        };
      })
      .addCase(getAccount.rejected, (state): AuthenticationState => {
        return {
          ...state,
          loading: false,
          fetchFailure: true,
          initializing: false,
        };
      });
  },
});

export const { logout, resetAuthState } = authenticationSlice.actions;

export default authenticationSlice.reducer;
